// @ts-nocheck
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Switch } from "@libraryadmin/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@libraryadmin/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Save,
  Building2,
  BookMarked,
  ShieldAlert,
  IndianRupee,
  Clock,
  BookX,
  AlertTriangle,
  UserX,
  CheckCircle2,
  Check,
  Zap,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import { getSettings, updateSettings } from "@libraryadmin/services/api";
import type { LibrarySettings } from "@libraryadmin/data/seed";

const Settings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: getSettings,
  });

  const [form, setForm] = useState<LibrarySettings | null>(null);

  useEffect(() => {
    if (settings) {
      setForm(settings);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast({
        title: "Settings Saved Successfully",
        description: "Library fine rules and circulation parameters updated.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form) {
      mutation.mutate(form);
    }
  };

  const handleApplySingleRule = (ruleKey: string, ruleName: string) => {
    if (!form) {
      return;
    }
    const todayStr = new Date().toISOString().split("T")[0];
    const updatedApplied = {
      ...(form.ruleAppliedDates || {}),
      [ruleKey]: todayStr,
    };

    const updatedForm = { ...form, ruleAppliedDates: updatedApplied };
    if (ruleKey === "late_return") {
      updatedForm.lateReturnRuleActive = true;
    }
    if (ruleKey === "lost_book") {
      updatedForm.lostBookRuleActive = true;
    }
    if (ruleKey === "damaged_book") {
      updatedForm.damagedBookRuleActive = true;
    }
    if (ruleKey === "membership_expiry") {
      updatedForm.membershipExpiryRuleActive = true;
    }

    setForm(updatedForm);
    mutation.mutate(updatedForm);
    toast({
      title: `${ruleName} Applied & Activated`,
      description: `Rule parameters updated and marked active on ${todayStr}.`,
    });
  };

  return (
    <DashboardLayout
      title="System Settings"
      subtitle="Configure library identity, circulation rules, fine management penalties, and heritage restrictions"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs defaultValue="fines" className="space-y-6">
          <div className="scrollbar-none w-full min-w-0 overflow-x-auto pb-1">
            <TabsList className="inline-flex h-auto w-max flex-nowrap gap-1 bg-muted/60 p-1">
              <TabsTrigger value="general" className="gap-2 whitespace-nowrap">
                <Building2 className="h-4 w-4 shrink-0" /> General Info
              </TabsTrigger>
              <TabsTrigger
                value="circulation"
                className="gap-2 whitespace-nowrap"
              >
                <BookMarked className="h-4 w-4 shrink-0" /> Circulation Rules
              </TabsTrigger>
              <TabsTrigger
                value="fines"
                className="gap-2 whitespace-nowrap font-semibold text-primary"
              >
                <IndianRupee className="h-4 w-4 shrink-0 text-primary" />{" "}
                Payment Rules
              </TabsTrigger>
              <TabsTrigger
                value="restrictions"
                className="gap-2 whitespace-nowrap"
              >
                <ShieldAlert className="h-4 w-4 shrink-0" /> Special Access &
                Heritage
              </TabsTrigger>
            </TabsList>
          </div>

          {/* General Tab */}
          <TabsContent value="general" className="animate-fade-in">
            <div className="form-section space-y-6">
              <h2 className="text-lg font-semibold">
                Library Identity & Address
              </h2>
              {isLoading || !form ? (
                <div className="py-8 text-center text-muted-foreground">
                  Loading settings...
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Library Name *</Label>
                    <Input
                      value={form.libraryName}
                      onChange={e =>
                        setForm({ ...form, libraryName: e.target.value })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Phone Contact</Label>
                    <Input
                      value={form.phone}
                      onChange={e =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Email Contact</Label>
                    <Input
                      value={form.email}
                      onChange={e =>
                        setForm({ ...form, email: e.target.value })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Working Hours</Label>
                    <Input
                      value={form.workingHours}
                      onChange={e =>
                        setForm({ ...form, workingHours: e.target.value })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={form.address}
                      onChange={e =>
                        setForm({ ...form, address: e.target.value })
                      }
                      className="input-field"
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Circulation Tab */}
          <TabsContent value="circulation" className="animate-fade-in">
            <div className="form-section space-y-6">
              <h2 className="text-lg font-semibold">
                Circulation Limits & Borrowing Policy
              </h2>
              {form && (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Max Books Allowed Per Member</Label>
                    <Input
                      type="number"
                      value={form.maxBooksPerMember}
                      onChange={e =>
                        setForm({
                          ...form,
                          maxBooksPerMember: Number(e.target.value),
                        })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Default Borrowing Period (Days)</Label>
                    <Input
                      type="number"
                      value={form.issueDurationDays}
                      onChange={e =>
                        setForm({
                          ...form,
                          issueDurationDays: Number(e.target.value),
                        })
                      }
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Standard Overdue Daily Fine (₹ / Day)</Label>
                    <Input
                      type="number"
                      value={form.finePerDay}
                      onChange={e =>
                        setForm({ ...form, finePerDay: Number(e.target.value) })
                      }
                      className="input-field"
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB: FINE MANAGEMENT RULES */}
          <TabsContent value="fines" className="animate-fade-in space-y-6">
            {form && (
              <>
                {/* APPLIED RULES SUMMARY TABLE */}
                <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="flex flex-col justify-between gap-2 border-b pb-3 sm:flex-row sm:items-center">
                    <div>
                      <h2 className="flex items-center gap-2 text-base font-bold text-foreground">
                        <Zap className="h-5 w-5 text-amber-500" /> Applied Fine
                        Rules & Status Summary
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Overview of active fine management policies and their
                        effective parameters.
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="self-start border-primary/30 bg-primary/10 text-primary sm:self-auto"
                    >
                      Live Configuration
                    </Badge>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 text-xs">
                          <TableHead>Fine Rule Name</TableHead>
                          <TableHead>Active / Inactive Status</TableHead>
                          <TableHead>Active Parameters Summary</TableHead>
                          <TableHead>Date Applied</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* 1. Late Return Rule */}
                        <TableRow className="text-xs">
                          <TableCell className="flex items-center gap-2 font-bold">
                            <Clock className="h-4 w-4 text-amber-500" />{" "}
                            Day-Wise Late Return Rule
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={form.lateReturnRuleActive ?? true}
                                onCheckedChange={checked =>
                                  setForm({
                                    ...form,
                                    lateReturnRuleActive: checked,
                                  })
                                }
                              />
                              <Badge
                                variant="outline"
                                className={
                                  form.lateReturnRuleActive
                                    ? "badge-success"
                                    : "badge-secondary"
                                }
                              >
                                {form.lateReturnRuleActive
                                  ? "Active"
                                  : "Inactive"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            Tier 1: ₹{form.fineRateTier1 ?? 5}/d • Tier 2: ₹
                            {form.fineRateTier2 ?? 10}/d • Tier 3: ₹
                            {form.fineRateTier3 ?? 20}/d (Grace:{" "}
                            {form.fineGracePeriodDays ?? 1}d, Cap: ₹
                            {form.fineMaxCap ?? 500})
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">
                            {form.ruleAppliedDates?.late_return || "2024-07-01"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                handleApplySingleRule(
                                  "late_return",
                                  "Day-Wise Late Return Rule"
                                )
                              }
                              className="btn-primary h-7 px-2.5 text-[11px]"
                            >
                              <Check className="mr-1 h-3 w-3" /> Apply Rule
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* 2. Lost Book Rule */}
                        <TableRow className="text-xs">
                          <TableCell className="flex items-center gap-2 font-bold">
                            <BookX className="h-4 w-4 text-red-500" /> Lost Book
                            Penalty Rule
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={form.lostBookRuleActive ?? true}
                                onCheckedChange={checked =>
                                  setForm({
                                    ...form,
                                    lostBookRuleActive: checked,
                                  })
                                }
                              />
                              <Badge
                                variant="outline"
                                className={
                                  form.lostBookRuleActive
                                    ? "badge-success"
                                    : "badge-secondary"
                                }
                              >
                                {form.lostBookRuleActive
                                  ? "Active"
                                  : "Inactive"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            Admin Fee: ₹{form.lostBookProcessingFee ?? 50} • MRP
                            Multiplier: {form.lostBookMrpMultiplier ?? 100}% •
                            Default Flat Fee: ₹{form.lostBookFlatFee ?? 500}
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">
                            {form.ruleAppliedDates?.lost_book || "2024-07-01"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                handleApplySingleRule(
                                  "lost_book",
                                  "Lost Book Penalty Rule"
                                )
                              }
                              className="btn-primary h-7 px-2.5 text-[11px]"
                            >
                              <Check className="mr-1 h-3 w-3" /> Apply Rule
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* 3. Damaged Book Rule */}
                        <TableRow className="text-xs">
                          <TableCell className="flex items-center gap-2 font-bold">
                            <AlertTriangle className="h-4 w-4 text-orange-500" />{" "}
                            Damaged Book Penalty Rule
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={form.damagedBookRuleActive ?? true}
                                onCheckedChange={checked =>
                                  setForm({
                                    ...form,
                                    damagedBookRuleActive: checked,
                                  })
                                }
                              />
                              <Badge
                                variant="outline"
                                className={
                                  form.damagedBookRuleActive
                                    ? "badge-success"
                                    : "badge-secondary"
                                }
                              >
                                {form.damagedBookRuleActive
                                  ? "Active"
                                  : "Inactive"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            Minor: ₹{form.damagedBookMinorFee ?? 150} •
                            Moderate: ₹{form.damagedBookModerateFee ?? 300} •
                            Severe: ₹{form.damagedBookSevereFee ?? 600}
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">
                            {form.ruleAppliedDates?.damaged_book ||
                              "2024-07-01"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                handleApplySingleRule(
                                  "damaged_book",
                                  "Damaged Book Penalty Rule"
                                )
                              }
                              className="btn-primary h-7 px-2.5 text-[11px]"
                            >
                              <Check className="mr-1 h-3 w-3" /> Apply Rule
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* 4. Membership Expiry Rule */}
                        <TableRow className="text-xs">
                          <TableCell className="flex items-center gap-2 font-bold">
                            <UserX className="h-4 w-4 text-[#a07a1c]" />{" "}
                            Membership Renewal Fee Rule
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={
                                  form.membershipExpiryRuleActive ?? true
                                }
                                onCheckedChange={checked =>
                                  setForm({
                                    ...form,
                                    membershipExpiryRuleActive: checked,
                                  })
                                }
                              />
                              <Badge
                                variant="outline"
                                className={
                                  form.membershipExpiryRuleActive
                                    ? "badge-success"
                                    : "badge-secondary"
                                }
                              >
                                {form.membershipExpiryRuleActive
                                  ? "Active"
                                  : "Inactive"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            Annual Membership Renewal Fee: ₹
                            {form.annualMembershipFee ?? 350} (1 Year Validity)
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">
                            {form.ruleAppliedDates?.membership_expiry ||
                              "2024-07-01"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                handleApplySingleRule(
                                  "membership_expiry",
                                  "Membership Renewal Fee Rule"
                                )
                              }
                              className="btn-primary h-7 px-2.5 text-[11px]"
                            >
                              <Check className="mr-1 h-3 w-3" /> Apply Rule
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* 1. Day-Wise Late Return Fine Rules */}
                <div className="form-section space-y-4">
                  <div className="flex flex-col justify-between gap-3 border-b pb-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-primary">
                      <Clock className="h-5 w-5 text-amber-500" />
                      <div>
                        <h2 className="text-base font-bold text-foreground">
                          1. Day-Wise Late Return Penalties
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Configure tiered daily fine rates depending on days
                          overdue past loan duration.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          Status:
                        </span>
                        <Switch
                          checked={form.lateReturnRuleActive ?? true}
                          onCheckedChange={checked =>
                            setForm({ ...form, lateReturnRuleActive: checked })
                          }
                        />
                        <span className="text-xs font-bold">
                          {form.lateReturnRuleActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          handleApplySingleRule(
                            "late_return",
                            "Day-Wise Late Return Rule"
                          )
                        }
                        className="btn-primary h-8 px-3 text-xs"
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Apply
                        Rule
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-3">
                    <div className="space-y-1.5 rounded-lg border border-border/40 bg-muted/30 p-3">
                      <Label className="flex items-center justify-between text-xs font-semibold">
                        <span>Tier 1: Days 1 – 7 Overdue</span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          Rate (₹/day)
                        </span>
                      </Label>
                      <Input
                        type="number"
                        value={form.fineRateTier1 ?? 5}
                        onChange={e =>
                          setForm({
                            ...form,
                            fineRateTier1: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Fine rate for 1st week of delay
                      </p>
                    </div>

                    <div className="space-y-1.5 rounded-lg border border-border/40 bg-muted/30 p-3">
                      <Label className="flex items-center justify-between text-xs font-semibold">
                        <span>Tier 2: Days 8 – 14 Overdue</span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          Rate (₹/day)
                        </span>
                      </Label>
                      <Input
                        type="number"
                        value={form.fineRateTier2 ?? 10}
                        onChange={e =>
                          setForm({
                            ...form,
                            fineRateTier2: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Increased rate for 2nd week
                      </p>
                    </div>

                    <div className="space-y-1.5 rounded-lg border border-border/40 bg-muted/30 p-3">
                      <Label className="flex items-center justify-between text-xs font-semibold">
                        <span>Tier 3: Days 15+ Overdue</span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          Rate (₹/day)
                        </span>
                      </Label>
                      <Input
                        type="number"
                        value={form.fineRateTier3 ?? 20}
                        onChange={e =>
                          setForm({
                            ...form,
                            fineRateTier3: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Severe overdue penalty rate
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 pt-1 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Grace Period (Days)</Label>
                      <Input
                        type="number"
                        value={form.fineGracePeriodDays ?? 1}
                        onChange={e =>
                          setForm({
                            ...form,
                            fineGracePeriodDays: Number(e.target.value),
                          })
                        }
                        className="input-field"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Days after due date before fines begin accruing
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Maximum Fine Cap Limit (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.fineMaxCap ?? 500}
                        onChange={e =>
                          setForm({
                            ...form,
                            fineMaxCap: Number(e.target.value),
                          })
                        }
                        className="input-field"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Maximum total overdue fine per book
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2. Lost Book Penalty Rules */}
                <div className="form-section space-y-4">
                  <div className="flex flex-col justify-between gap-3 border-b pb-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-primary">
                      <BookX className="h-5 w-5 text-red-500" />
                      <div>
                        <h2 className="text-base font-bold text-foreground">
                          2. Lost Book Penalty Settings
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Set replacement fees, MRP multipliers, and
                          administrative processing charges.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          Status:
                        </span>
                        <Switch
                          checked={form.lostBookRuleActive ?? true}
                          onCheckedChange={checked =>
                            setForm({ ...form, lostBookRuleActive: checked })
                          }
                        />
                        <span className="text-xs font-bold">
                          {form.lostBookRuleActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          handleApplySingleRule(
                            "lost_book",
                            "Lost Book Penalty Rule"
                          )
                        }
                        className="btn-primary h-8 px-3 text-xs"
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Apply
                        Rule
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Administrative Processing Fee (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.lostBookProcessingFee ?? 50}
                        onChange={e =>
                          setForm({
                            ...form,
                            lostBookProcessingFee: Number(e.target.value),
                          })
                        }
                        className="input-field"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Flat admin charge added to book cost
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Book MRP Charge Multiplier (%)
                      </Label>
                      <Input
                        type="number"
                        value={form.lostBookMrpMultiplier ?? 100}
                        onChange={e =>
                          setForm({
                            ...form,
                            lostBookMrpMultiplier: Number(e.target.value),
                          })
                        }
                        className="input-field"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        100% = original MRP, 150% = 1.5x MRP
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Flat Fee if MRP Unknown (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.lostBookFlatFee ?? 500}
                        onChange={e =>
                          setForm({
                            ...form,
                            lostBookFlatFee: Number(e.target.value),
                          })
                        }
                        className="input-field"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Default charge if MRP is missing
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3. Damaged Book Penalty Rules */}
                <div className="form-section space-y-4">
                  <div className="flex flex-col justify-between gap-3 border-b pb-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-primary">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      <div>
                        <h2 className="text-base font-bold text-foreground">
                          3. Damaged Book Penalty Charges
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Define penalty tiers based on book damage severity
                          reported at book return.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          Status:
                        </span>
                        <Switch
                          checked={form.damagedBookRuleActive ?? true}
                          onCheckedChange={checked =>
                            setForm({ ...form, damagedBookRuleActive: checked })
                          }
                        />
                        <span className="text-xs font-bold">
                          {form.damagedBookRuleActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          handleApplySingleRule(
                            "damaged_book",
                            "Damaged Book Penalty Rule"
                          )
                        }
                        className="btn-primary h-8 px-3 text-xs"
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Apply
                        Rule
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                      <Label className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                        Minor Damage Penalty (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.damagedBookMinorFee ?? 150}
                        onChange={e =>
                          setForm({
                            ...form,
                            damagedBookMinorFee: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Torn pages, pencil marks, cover bends
                      </p>
                    </div>

                    <div className="space-y-1.5 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
                      <Label className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                        Moderate Damage Penalty (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.damagedBookModerateFee ?? 300}
                        onChange={e =>
                          setForm({
                            ...form,
                            damagedBookModerateFee: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Water stains, binding damage, ink spills
                      </p>
                    </div>

                    <div className="space-y-1.5 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                      <Label className="text-xs font-semibold text-red-700 dark:text-red-400">
                        Severe Damage Penalty (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.damagedBookSevereFee ?? 600}
                        onChange={e =>
                          setForm({
                            ...form,
                            damagedBookSevereFee: Number(e.target.value),
                          })
                        }
                        className="input-field font-semibold"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Destroyed pages or unusable condition
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4. Membership Renewal Fee Settings */}
                <div className="form-section space-y-4">
                  <div className="flex flex-col justify-between gap-3 border-b pb-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-primary">
                      <UserX className="h-5 w-5 text-[#a07a1c]" />
                      <div>
                        <h2 className="text-base font-bold text-foreground">
                          4. Membership Expiry & Renewal Fees
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Set annual membership renewal fee charged upon
                          expiration.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          Status:
                        </span>
                        <Switch
                          checked={form.membershipExpiryRuleActive ?? true}
                          onCheckedChange={checked =>
                            setForm({
                              ...form,
                              membershipExpiryRuleActive: checked,
                            })
                          }
                        />
                        <span className="text-xs font-bold">
                          {form.membershipExpiryRuleActive
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          handleApplySingleRule(
                            "membership_expiry",
                            "Membership Renewal Fee Rule"
                          )
                        }
                        className="btn-primary h-8 px-3 text-xs"
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Apply
                        Rule
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Annual Membership Renewal Fee (₹)
                      </Label>
                      <Input
                        type="number"
                        value={form.annualMembershipFee ?? 350}
                        onChange={e =>
                          setForm({
                            ...form,
                            annualMembershipFee: Number(e.target.value),
                          })
                        }
                        className="input-field font-bold text-primary"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Standard 1-year membership extension fee
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Special Restrictions Tab */}
          <TabsContent value="restrictions" className="animate-fade-in">
            <div className="form-section space-y-6">
              <h2 className="text-lg font-semibold">
                Rare Books & Heritage Restrictions
              </h2>
              {form && (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Age Threshold for Restricted Books (Years)</Label>
                    <Input
                      type="number"
                      value={form.restrictedAgeThreshold}
                      onChange={e =>
                        setForm({
                          ...form,
                          restrictedAgeThreshold: Number(e.target.value),
                        })
                      }
                      className="input-field"
                    />
                    <p className="text-xs text-muted-foreground">
                      Books older than this published year count will require
                      Senior Librarian approval to issue.
                    </p>
                  </div>

                  <div className="flex flex-col justify-center space-y-2">
                    <label className="flex cursor-pointer items-center gap-3 pt-4">
                      <input
                        type="checkbox"
                        checked={form.autoApproveMembers}
                        onChange={e =>
                          setForm({
                            ...form,
                            autoApproveMembers: e.target.checked,
                          })
                        }
                        className="h-5 w-5 rounded border-border text-primary"
                      />
                      <div>
                        <span className="block text-sm font-semibold">
                          Auto-Approve New Member Registrations
                        </span>
                        <span className="text-xs text-muted-foreground">
                          If unchecked, new members stay in Pending status until
                          approved in the Approval queue.
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 border-t pt-4">
          <Button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary"
          >
            <Save className="mr-2 h-4 w-4" />
            {mutation.isPending ? "Saving Settings..." : "Save All Settings"}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
};

export default Settings;
